import { filter, find, forEach, get, isEmpty, startCase } from 'lodash';
import * as React from 'react';
import { differenceInDays, endOfDay, parse, startOfDay } from 'date-fns';
import { IPolicyApplication } from '../data/api/models';
import { ICalculationError } from '../services/quote/models';

export const TITLES = ['Mrs', 'Miss', 'Mr', 'Ms', 'Mx', 'Dr']

export const getBenefit = (benefits: any[], displayName, type?, subBenefitName?) => {
  let displayBenefit = '';
  let displayBenefitDescription = '';
  let subBenefit = [];
  let displaySubBenefit = '';
  let displaySubBenefitDescription = '';

  if (!benefits) {
    return
  }

  benefits.forEach((benefit) => {
    if (benefit.displayName === displayName || benefit.name === displayName || benefit.display_name === displayName) {
      subBenefit = benefit.subBenefits || benefit.sub_benefits;
      displayBenefit = benefit.text;
      displayBenefitDescription = benefit.description;
    }
    forEach(subBenefit, (item) => {
      if (item.name === subBenefitName) {
        displaySubBenefit = item.text;
        displaySubBenefitDescription = item.description;
      }
    });
  });

  switch (type) {
    case 'subBenefit':
      return displaySubBenefit;
    case 'subBenefitDescription':
      return displaySubBenefitDescription;
    case 'description':
      return displayBenefitDescription;
    default:
      return displayBenefit;
  }
}

export const standardiseTheName = (name) => startCase(name.replace('-', ' '));

export const mentalHealthLinkRenderer = (): any => {
  return (
    <a href={'https://www.admiral.com/magazine/guides/travel/travel-and-mental-health-guide'}
       target="_blank">here
    </a>
  );
}

export const replaceBenefitTextPlaceHolder = (
  text: string,
  benefits: any[],
  benefitOptionName: string,
  isMta: boolean,
  coverLevel: string
) => {
  switch (benefitOptionName) {
    case 'gadget_cover':
      const toReplace = '{{single_item_value}}'
      let gadgetBenefit;
      let singleItemLimit;

      if (isMta) {
        gadgetBenefit = find(benefits, ['short_name', 'Gadget Cover'])
        singleItemLimit = gadgetBenefit ? find(gadgetBenefit.sub_benefits, ['name', 'Single item limit']) : null;
        if (coverLevel) {
          singleItemLimit.text = get(singleItemLimit, `cover_level.${coverLevel}`)
        }
      } else {
        gadgetBenefit = find(benefits, ['shortName', 'Gadget Cover'])
        singleItemLimit = gadgetBenefit ? find(gadgetBenefit.subBenefits, ['name', 'Single item limit']) : null;
      }

      text = singleItemLimit ? text.replace(toReplace, singleItemLimit.text) : text;
      return text;
    default:
      return text;
  }
}


export const getTripDuration = (startDate, endDate, addDays) => {
  startDate = startOfDay(parse(startDate, 'dd/MM/yyyy', new Date()));
  endDate = endOfDay(parse(endDate, 'dd/MM/yyyy', new Date()));

  if (!addDays) {
    addDays = 0;
  }

  return differenceInDays(endDate, startDate) + addDays;
}

export const getPremiumErrorMessage = (formValues: IPolicyApplication, quotes): ICalculationError => {

  let noAvailableQuotes = false;
  let oneOrMoreQuote0Premium = false;

  let errors = '';
  let referrals = [];
  const tripType = get(formValues, 'tripType');
  const isSingle = tripType === 'single';
  const isAnnual = tripType === 'annual';

  forEach(quotes, (tripTypes) => {
    if (!tripTypes) {
      return;
    }

    const singleTripQuote = tripTypes['Single Trip'];
    const annualTripQuote = tripTypes['Annual Multi Trip'];

    const errorTypes = ['errors', 'declines', 'referrals'];
    noAvailableQuotes = errorTypes.some((errorType) => {
      return isSingle && checkHasProperty(singleTripQuote, errorType)
        || isAnnual && checkHasProperty(annualTripQuote, errorType);
    });

    if ((isSingle && singleTripQuote && singleTripQuote.gross <= 0)
      || (isAnnual && annualTripQuote && annualTripQuote.gross <= 0)) {
      oneOrMoreQuote0Premium = true;
    }

    if (isSingle && checkHasProperty(singleTripQuote, 'errors')) {
      errors = singleTripQuote.errors;
    }

    if (isAnnual && checkHasProperty(annualTripQuote, 'errors')) {
      errors = annualTripQuote.errors;
    }

    if (isSingle && checkHasProperty(singleTripQuote, 'referrals')) {
      referrals = singleTripQuote.referrals;
    }

    if (isAnnual && checkHasProperty(annualTripQuote, 'referrals')) {
      referrals = annualTripQuote.referrals;
    }

  });

  return errorMessage(
    errors,
    referrals,
    oneOrMoreQuote0Premium,
    noAvailableQuotes,
  );

}

const hasScreeningRegionErrors = (errors) => {
  let match = [];
  if (errors && errors.length) {
    errors.forEach((e) => {
      match = e.match('The screening region is incorrect  - please re-screen');
    });
  }
  return match ? match.length : 0;
}

const checkHasProperty = (quote, check) => quote && quote[check] && quote[check].length > 0;

const errorMessage = (
  errors,
  referrals,
  oneOrMoreQuote0Premium,
  noAvailableQuotes,
): ICalculationError => {

  const genericMessage = 'Unfortunately, based on the information provided we are unable to offer you quote.'


  if (!isEmpty(referrals)) {
    const getMedicalDeclines = filter(referrals, (rules) => {
      return get(rules, 'is_medical', false);
    });

    const maxAgeExceededForWinterSports = referrals.some((referral) => {
      return referral.name === 'max_age' && referral.option === 'winter_sports';
    });

    const maxDurationExceededForWinterSports = referrals.some((referral) => {
      return referral.name === 'trip_duration' && referral.option === 'winter_sports';
    });

    const hasAgeReferrals = referrals.some((referral) => {
      return referral.name === 'max_age' && !referral.option;
    });

    if (hasAgeReferrals) {
      return {
        type: 'age-decline',
        referrals,
        message: '',
        component: (
          <span>
            <br/>
            <p>
              Unfortunately, we’re unable to give you a quote on this product as our policies have an age restriction.
            </p>
            <p>But there are alternatives available that can help you to find cover. The British Insurance Brokers'
              Association (BIBA) has a list of regulated brokers that provide specialist travel insurance and will
              be able to point you in the right direction. Call
              BIBA on 0370 950 1790* or visit their
              website <a target="_blank" href="https://www.biba.org.uk/find-insurance/">
                www.biba.org.uk/find-insurance
              </a>.
              *Lines are open 9am to 5pm, Monday to Friday.</p>
          </span>
        )
      }
    }

    if (maxAgeExceededForWinterSports || maxDurationExceededForWinterSports) {
      return {
        referrals,
        type: 'winter-sports-decline',
        message: 'Sorry we are unable to provide you for a quote for Winter Sports Cover.',
      }
    }

    if (!getMedicalDeclines.length) {
      return {
        referrals,
        type: 'quote-decline',
        message: genericMessage,
      }
    }
  }

  if (!isEmpty(errors)) {
    const screeningRegionError = hasScreeningRegionErrors(errors) > 0;

    const hasAgeErrors = Array.isArray(errors) && errors.filter(
      (error) => ['max age', 'above age criteria']
        .some((message) => error.toLowerCase().indexOf(message) >= 0),
    ).length;

    if (hasAgeErrors) {
      return {
        type: 'age-decline',
        message: '',
        component: (
          <span>
            <br/>
            <p>
              Unfortunately, we’re unable to give you a quote on this product as our policies have an age restriction.
            </p>
            <p>But there are alternatives available that can help you to find cover. The British Insurance Brokers'
              Association (BIBA) has a list of regulated brokers that provide specialist travel insurance and will
              be able to point you in the right direction. Call
              BIBA on 0370 950 1790* or visit their
              website <a target="_blank" href="https://www.biba.org.uk/find-insurance/">
                www.biba.org.uk/find-insurance
              </a>.
              *Lines are open 9am to 5pm, Monday to Friday.</p>
          </span>
        )
      }
    }

    if (screeningRegionError) {
      return {
        type: 'quote-decline',
        message: genericMessage,
      }
    }
  }

  if (noAvailableQuotes) {
    return {
      type: 'quote-decline',
      message: genericMessage,
    }
  }

  if (oneOrMoreQuote0Premium) {
    return {
      type: 'quote-decline',
      message: genericMessage,
    }
  }

  return null;
}

export const shouldIgnoreCardPermissions = (): boolean => {
  return true;
}
